/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { DiagnosticSeverity, RETRY_POLICY } from "../../../model/types";
import type { TRetryOptions } from "../../../model/types";
import {
  SignInFlowEvent,
  SignInLogger
} from "../../../model/logging/SignInLogger";
import { formatRetryErrorMessage } from "../../../model/utility";
import { Locale, LocaleKeys } from "../../../model/locale";
import { showAlertOverlay, dismissAlertOverlay } from "../../alertOverlay";
import type { AlertOverlayAction } from "../../alertOverlay";
import {
  buildConnectionRetryActions,
  CONNECTION_WARMUP_RETRY_DELAYS_MS,
  getRetryNowSignal,
  isConnectionWarmupAttempt
} from "../../connectionRetry";
import {
  ALERT_REASON_RETRY_RECOVERED,
  ALERT_REASON_RETRY_TERMINAL,
  ALERT_TITLE_CONNECTION_ERROR,
  HTTP_STATUS_FORBIDDEN,
  HTTP_STATUS_UNAUTHORIZED,
  OWNER_SIGNIN_RETRY,
  SIGN_IN_AGAIN_RETRY_ACTION_ID
} from "../signIn.constants";
import { extractHttpStatus, extractSignInErrorCode } from "./authErrors";
import {
  isTerminalServiceErrorCode,
  isTerminalSignInError
} from "./signInErrorActions";
import {
  ErrorTypes,
  getHttpContext,
  getServiceErrorCode
} from "../../../model/error";

/**
 * The code a mapped error was *built* with, before `extractSignInErrorCode`
 * validates it. That function reports the catalog-colliding families (`/bind`
 * A700-A709, …) as A010 for telemetry, which is right for what the user and
 * support see but hides the cause from the terminality check.
 */
function readRawErrorCode(error: unknown): string {
  if (error === null || error === undefined || typeof error !== "object") {
    return "";
  }
  const candidate = error as { getCode?: () => string };
  if (typeof candidate.getCode !== "function") {
    return "";
  }
  const code: unknown = candidate.getCode();
  return typeof code === "string" ? code : "";
}

/**
 * DPS `403 AUTHS.NOT_AUTHORIZED` with a `Retry-After` header means "account
 * suspended": the schema asks the client to keep retrying on the server's
 * schedule so the device recovers on its own once the bill is paid. Without the
 * header the same code is a definitive refusal and stays terminal.
 */
function isSuspendedAccountAwaitingRecovery(error: unknown): boolean {
  if (
    readRawErrorCode(error) !== ErrorTypes.DEVICE_PROVISION_NOT_AUTHORIZED_A462
  ) {
    return false;
  }
  const context = getHttpContext(error);
  return (
    context !== null &&
    typeof context.headers["retry-after"] === "string" &&
    context.headers["retry-after"].length > 0
  );
}

/**
 * Whether a sign-in failure is a definitive answer that re-issuing the same
 * call cannot change — the shared terminality rule for every sign-in retry,
 * blocking (`createCriticalRetryOptions`) or silent (`createSilentRetryOptions`).
 *
 * Checks, in order: the reported catalog code; the pre-masking code (families
 * reported as A010, such as `/bind`); the backend body code for failures with
 * no catalog number yet (DPS 409 / 410); and HTTP 401 / 403 whatever the code.
 *
 * The status is only visible because the interactors re-attach the HTTP
 * response to the error they map (`attachHttpContext`).
 */
export function isDefinitiveSignInFailure(error: unknown): boolean {
  const httpStatus: number | null = extractHttpStatus(error);
  return (
    isTerminalSignInError(extractSignInErrorCode(error)) ||
    isTerminalSignInError(readRawErrorCode(error)) ||
    isTerminalServiceErrorCode(getServiceErrorCode(error)) ||
    httpStatus === HTTP_STATUS_UNAUTHORIZED ||
    httpStatus === HTTP_STATUS_FORBIDDEN
  );
}

/**
 * The narrow slice of a Redux dispatch the retry overlay needs: it only ever
 * dispatches the two alert-overlay actions. Every thunkAPI dispatch satisfies
 * this, so the helper works from any sign-in thunk without dragging the store
 * types into this module.
 */
export type TAlertDispatch = (
  action:
    | ReturnType<typeof showAlertOverlay>
    | ReturnType<typeof dismissAlertOverlay>
) => unknown;

/**
 * Optional behaviour for {@link createCriticalRetryOptions}.
 */
export interface TCriticalRetryOverlayOptions {
  /**
   * The attempt's session signal (`beginSignInSession`). When given, the
   * overlay also offers "Sign In Again", which aborts this signal so the user
   * can leave a retry that is not recovering and correct what they typed.
   * Once it is aborted the loop stops retrying and raises no further overlay.
   */
  signInAgainSignal?: AbortSignal;
}

/** The overlay's buttons: Exit (TV only) + Retry, then optional Sign In Again. */
function buildSignInRetryActions(
  offerSignInAgain: boolean
): AlertOverlayAction[] {
  const actions: AlertOverlayAction[] = buildConnectionRetryActions();
  if (offerSignInAgain) {
    // Appended so the first button — focused on show, and the one BACK
    // selects — stays what it was.
    actions.push({
      id: SIGN_IN_AGAIN_RETRY_ACTION_ID,
      label: Locale.getString(LocaleKeys.SIGN_IN_ACTION_SIGN_IN_AGAIN)
    });
  }
  return actions;
}

/**
 * CRITICAL retry options that surface the connection-error overlay while a
 * call is retrying and dismiss it once the call finally succeeds.
 *
 * Every blocking sign-in service call (claim, bind, domain token, provision,
 * license plate, …) wants exactly this behaviour, so they share one factory
 * rather than repeating the two callbacks at each call site.
 *
 * The overlay carries Exit (TV only) and Retry actions: Retry wakes this same
 * loop through {@link getRetryNowSignal} so the failed call is re-issued
 * immediately without a second request, and Exit closes the app. With
 * `options.signInAgainSignal` it also offers Sign In Again, which ends the
 * attempt and returns to the credential form. The retry_overlay_shown /
 * retry_overlay_dismissed diagnostics make the blocking window visible in
 * telemetry.
 */
export function createCriticalRetryOptions(
  dispatch: TAlertDispatch,
  options: TCriticalRetryOverlayOptions = {}
): TRetryOptions {
  // Per-invocation, so each service call tracks its own overlay window.
  let overlayShownAt: number | null = null;
  let retryCount: number = 0;
  const signInAgainSignal: AbortSignal | undefined = options.signInAgainSignal;
  const isSignInAbandoned = (): boolean =>
    signInAgainSignal !== undefined && signInAgainSignal.aborted;

  return {
    retryPolicy: RETRY_POLICY.CRITICAL,
    // Retry silently at [1s, 3s, 5s] before the overlay is surfaced.
    initialRetryDelaysMs: CONNECTION_WARMUP_RETRY_DELAYS_MS,
    // Read fresh before each backoff so a manual Retry can collapse the wait.
    getRetryNowSignal: getRetryNowSignal,
    /**
     * Keeps definitively-rejected calls out of CRITICAL backoff entirely.
     *
     * Without this a 401 NOT_AUTHORIZED or a 409 TOO_MANY_DEVICES retried
     * forever — CRITICAL never gives up and skips the retryability check —
     * while the actionless overlay below stayed on screen. The device was
     * stuck with no user escape until the app was killed. The error now
     * propagates to the sign-in error alert, which offers the actions that
     * cause actually permits (UAW-2416 AC-4 / D1).
     *
     * The rule itself is {@link isDefinitiveSignInFailure}; this adds the one
     * exception a blocking flow can afford — an account suspension that asks
     * to be retried — and takes the overlay down on the way out.
     */
    isTerminal: (error: unknown): boolean => {
      // The user chose Sign In Again while a request was in flight: its
      // failure must end the loop, not re-raise the overlay they just left.
      if (isSignInAbandoned()) {
        return true;
      }
      // Checked first: it is the one 403 that must keep retrying, so neither
      // the code list (A462 is terminal) nor the status backstop may claim it.
      if (isSuspendedAccountAwaitingRecovery(error)) {
        return false;
      }
      const terminal: boolean = isDefinitiveSignInFailure(error);
      if (terminal && overlayShownAt !== null) {
        // The overlay is up from an earlier attempt in this same call; the
        // error is about to propagate, so take it down rather than leave it
        // covering the alert that replaces it.
        overlayShownAt = null;
        dispatch(dismissAlertOverlay(ALERT_REASON_RETRY_TERMINAL));
      }
      return terminal;
    },
    onRetry: (error: unknown, attempt: number): void => {
      // Stay silent through the warm-up retries; surface the overlay only once
      // they are exhausted.
      if (isConnectionWarmupAttempt(attempt)) {
        return;
      }
      retryCount = retryCount + 1;
      if (overlayShownAt === null) {
        overlayShownAt = Date.now();
        const fields = {
          attempt: String(retryCount),
          error_type: SignInLogger.describeErrorType(error),
          http_status: SignInLogger.extractHttpStatus(error)
        };
        SignInLogger.logFlowEvent(
          SignInFlowEvent.RetryOverlayShown,
          "Connection error overlay shown while retrying",
          DiagnosticSeverity.Warning,
          fields
        );
        // Also operational, and alertable: diagnostics are backhaul-gated, so
        // without this a fleet of devices stuck behind the actionless overlay
        // would be invisible.
        SignInLogger.logSignInBlocked(fields);
      }
      dispatch(
        showAlertOverlay({
          title: ALERT_TITLE_CONNECTION_ERROR,
          message: formatRetryErrorMessage(error),
          actions: buildSignInRetryActions(signInAgainSignal !== undefined),
          owner: OWNER_SIGNIN_RETRY
        })
      );
    },
    onSuccess: (): void => {
      if (overlayShownAt !== null) {
        SignInLogger.logFlowEvent(
          SignInFlowEvent.RetryOverlayDismissed,
          "Connection error overlay dismissed after recovery",
          DiagnosticSeverity.Info,
          {
            attempt_count: String(retryCount),
            visible_ms: String(Date.now() - overlayShownAt)
          }
        );
        overlayShownAt = null;
      }
      dispatch(dismissAlertOverlay(ALERT_REASON_RETRY_RECOVERED));
    }
  };
}
